/*
 * Settings, Agency, Workspaces (LEA-476 slice 4; docs/2026-11-02-lea-476-workspace-add-and-offboard-plan.md,
 * "Web"). The Email Bison workspaces this agency handles, adding one by its key, and removing one.
 * Every route is organization-scoped and needs `org_admin`; the API refuses before the handler
 * (`app.ts`, `WORK_ROUTE_MATRIX`), and the section is listed only once the list has answered, as People is.
 *
 * Laid out as People is: add opens one sheet under the heading, and a removal asks inline under its
 * row, with the name typed to confirm, rather than in a modal over the page.
 */
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { z } from "zod";
import { ApiError, getJson } from "../http.ts";
import { UNREACHABLE } from "../shell/account-copy.ts";
import { AddWorkspaceForm, post } from "./workspace-add.tsx";
import {
  DRAFTING_STAYS_OFF,
  leftSentence,
  type Managed,
  ManagedSchema,
  REMOVE_REASON,
  removeRefusal,
  removingCopy,
  rowImport,
  rowMeta,
  rowState,
} from "./workspace-copy.ts";

/** How often the list is read again while a removal runs, so a finished one leaves on its own. */
const REMOVING_POLL_MS = 3_000;
/** How often while a workspace imports, so its count moves and Importing turns Active on its own. */
const IMPORTING_POLL_MS = 15_000;

const manageQuery = () =>
  queryOptions({
    queryKey: ["workspaces-manage"],
    staleTime: 10_000,
    retry: false,
    queryFn: async ({ signal }) =>
      z
        .object({ workspaces: z.array(ManagedSchema) })
        .parse(await getJson("/api/work/workspaces/manage", signal)).workspaces,
    refetchInterval: (query) => {
      const rows = query.state.data ?? [];
      if (rows.some((row) => row.running)) return REMOVING_POLL_MS;
      return rows.some((row) => rowImport(row) !== null) ? IMPORTING_POLL_MS : false;
    },
  });

/** Keep a previously admitted page mounted for error recovery, as People does. */
export function useManagesWorkspaces(): boolean {
  return useQuery(manageQuery()).data !== undefined;
}

/**
 * Whether this person may add workspaces, read only where it is asked (`enabled`): the workspace
 * switcher's "Add workspace" (LEA-501), which asks once it opens or where there is one workspace.
 */
export function useCanAddWorkspaces(enabled: boolean): boolean {
  return useQuery({ ...manageQuery(), enabled }).data !== undefined;
}

/**
 * Whether the API has answered who may add workspaces, and its answer: where bare `/settings` lands
 * for a person with no workspace waits for it rather than guessing (LEA-501).
 */
export function useWorkspacesManagement(): {
  readonly settled: boolean;
  readonly manages: boolean;
} {
  const manage = useQuery(manageQuery());
  return { settled: !manage.isPending, manages: manage.data !== undefined };
}

type Notice = { readonly message: string; readonly alert: boolean };

function NoticeLine(props: { notice: Notice }) {
  return (
    <div
      className={props.notice.alert ? "lcr-people-notice is-alert" : "lcr-people-notice"}
      role={props.notice.alert ? "alert" : "status"}
    >
      <p>{props.notice.message}</p>
    </div>
  );
}

function Unavailable(props: { error: unknown; onRetry: () => void }) {
  if (props.error instanceof ApiError && props.error.refused)
    return (
      <p className="lcr-settings-note">Only agency owners and admins add and remove workspaces.</p>
    );
  const signedOut = props.error instanceof ApiError && props.error.signedOut;
  return (
    <p className="lcr-settings-note" role="alert">
      {signedOut ? "You were signed out. Reload the page to sign in again." : UNREACHABLE}{" "}
      {!signedOut && (
        <button type="button" className="lcr-linklike" onClick={props.onRetry}>
          Try again
        </button>
      )}
    </p>
  );
}

/** `currentWorkspaceId` marks the workspace on screen; null where the agency has none open (LEA-501). */
export function WorkspacesSection(props: { readonly currentWorkspaceId: string | null }) {
  const cache = useQueryClient();
  const list = useQuery(manageQuery());
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  // The rail's workspace menu reads its own list, so both are read again after any write.
  const refresh = () =>
    Promise.all([
      cache.invalidateQueries({ queryKey: ["workspaces-manage"] }),
      cache.invalidateQueries({ queryKey: ["workspaces"] }),
    ]);
  const open = () => {
    setNotice(null);
    setAdding(true);
  };

  return (
    <section
      className="lcr-settings-section lcr-workspaces"
      aria-labelledby="lcr-settings-title"
      aria-busy={list.isPending || undefined}
    >
      <div className="lcr-people-head">
        <h1 id="lcr-settings-title" className="lcr-page-title">
          Workspaces
        </h1>
        {list.isSuccess && !adding && (
          <button type="button" className="lcr-primary" onClick={open}>
            Add Bison workspace
          </button>
        )}
      </div>
      <p className="lcr-settings-note">
        The Email Bison workspaces this agency answers replies in. A workspace added here imports
        its replies into the inbox. {DRAFTING_STAYS_OFF}
      </p>
      {notice && <NoticeLine notice={notice} />}
      {list.isPending && <p className="lcr-settings-note">Loading this agency's workspaces…</p>}
      {list.isError && <Unavailable error={list.error} onRetry={() => void list.refetch()} />}
      {adding && list.isSuccess && (
        <AddWorkspaceForm
          onCancel={() => setAdding(false)}
          onChanged={() => void refresh()}
          onAdded={(message) => {
            setAdding(false);
            setNotice({ message, alert: false });
          }}
        />
      )}
      {list.data && list.data.length > 0 && (
        <ul className="lcr-people-list" aria-label="Workspaces in this agency">
          {list.data.map((row) => (
            <WorkspaceRow
              key={row.id}
              row={row}
              current={row.id === props.currentWorkspaceId}
              onAddKey={open}
              onNotice={setNotice}
              onChanged={refresh}
            />
          ))}
        </ul>
      )}
      {list.data?.length === 0 && !adding && (
        <p className="lcr-settings-note">
          No workspaces yet. Add one with an API key from Email Bison.
        </p>
      )}
    </section>
  );
}

function WorkspaceRow(props: {
  row: Managed;
  current: boolean;
  onAddKey: () => void;
  onNotice: (notice: Notice | null) => void;
  onChanged: () => Promise<unknown>;
}) {
  const { row } = props;
  const [confirming, setConfirming] = useState(false);
  const [resuming, setResuming] = useState(false);
  const removeButton = useRef<HTMLButtonElement>(null);
  const wasConfirming = useRef(false);
  useEffect(() => {
    if (wasConfirming.current && !confirming) removeButton.current?.focus();
    wasConfirming.current = confirming;
  }, [confirming]);
  const state = rowState(row);
  const stuck = row.state === "offboarding" && !row.running;

  const resume = async (): Promise<void> => {
    setResuming(true);
    props.onNotice(null);
    const answer = await offboard(row, row.name);
    setResuming(false);
    props.onNotice(removalNotice(answer, row.name, true));
    await props.onChanged();
  };

  return (
    <li className="lcr-people-row lcr-workspace-row">
      <div className="lcr-people-who">
        <span className="lcr-people-name">
          {row.name}
          {props.current && <span className="lcr-people-you"> (open now)</span>}
        </span>
        <span
          className="lcr-state lcr-state-compact"
          data-tone={state.tone === "good" ? undefined : state.tone}
        >
          <span className="lcr-state-dot" aria-hidden="true" />
          {state.label}
        </span>
      </div>
      <p className="lcr-people-meta">{rowMeta(row)}</p>
      {stuck && <p className="lcr-people-meta lcr-workspace-left">{leftSentence(row.left)}</p>}
      {row.state === "onboarded" && !confirming && (
        <div className="lcr-people-actions">
          {row.hasKey === false && (
            <button type="button" className="lcr-small-button" onClick={props.onAddKey}>
              Add key
            </button>
          )}
          <button
            ref={removeButton}
            type="button"
            className="lcr-small-button lcr-ghost"
            onClick={() => {
              props.onNotice(null);
              setConfirming(true);
            }}
          >
            Remove
          </button>
        </div>
      )}
      {stuck && (
        <div className="lcr-people-actions">
          <button
            type="button"
            className="lcr-small-button"
            disabled={resuming}
            onClick={() => void resume()}
          >
            {resuming ? "Resuming…" : "Resume removal"}
          </button>
        </div>
      )}
      {confirming && (
        <RemoveForm
          row={row}
          current={props.current}
          onCancel={() => setConfirming(false)}
          onDone={async (notice) => {
            setConfirming(false);
            props.onNotice(notice);
            await props.onChanged();
          }}
        />
      )}
    </li>
  );
}

const offboard = (row: Managed, confirmName: string) =>
  post(`/api/work/workspaces/${encodeURIComponent(row.id)}/offboard`, {
    confirmName,
    reason: REMOVE_REASON,
  });

function removalNotice(
  answer: { status: number; body: unknown },
  name: string,
  resumed: boolean,
): Notice {
  const started = z.object({ state: z.string() }).safeParse(answer.body);
  if (answer.status === 202 && started.success)
    return { message: removingCopy(name, started.data.state, resumed), alert: false };
  return { message: removeRefusal({ ...answer, retryAfter: null }, name), alert: true };
}

function RemoveForm(props: {
  row: Managed;
  current: boolean;
  onCancel: () => void;
  onDone: (notice: Notice) => Promise<void>;
}) {
  const { row } = props;
  const id = useId();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="lcr-people-confirm lcr-workspace-remove"
      aria-labelledby={`${id}-title`}
      noValidate
      onKeyDown={(event) => {
        if (event.key === "Escape") props.onCancel();
      }}
      onSubmit={async (event) => {
        event.preventDefault();
        if (busy) return;
        if (typed.trim() !== row.name.trim()) {
          setError(`The name does not match. Type it as shown: ${row.name}.`);
          return;
        }
        setBusy(true);
        setError(null);
        const answer = await offboard(row, typed);
        setBusy(false);
        const notice = removalNotice(answer, row.name, false);
        // A refusal about the name stays here, beside the field; any other answer goes to the list.
        if (notice.alert && answer.status === 422) setError(notice.message);
        else await props.onDone(notice);
      }}
    >
      <h3 id={`${id}-title`} className="lcr-workspace-remove-title">
        Remove {row.name} from LeadCue?
      </h3>
      <ul className="lcr-workspace-effects">
        <li>
          LeadCue stops reading and processing it: no new replies come in, nothing is drafted.
        </li>
        <li>
          LeadCue stops using its Email Bison key and disconnects HubSpot from it. The key itself
          keeps working in Email Bison until you remove it there.
        </li>
        <li>It leaves this list and the workspace menu for everyone in the agency.</li>
      </ul>
      <p>
        Its mail stays in Email Bison, and adding it again with a key brings it back. A reply still
        waiting on a lead's qualification can hold the removal part way; it then shows here as
        Stuck, with Resume.
      </p>
      {props.current && (
        <p>You have this workspace open now. Once it is removed, choose another from the menu.</p>
      )}
      <label className="lcr-people-field">
        Type {row.name} to confirm
        <input
          type="text"
          autoComplete="off"
          spellCheck={false}
          // biome-ignore lint/a11y/noAutofocus: the form opens because the person asked to remove.
          autoFocus
          value={typed}
          aria-invalid={error !== null || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={(event) => {
            setTyped(event.target.value);
            setError(null);
          }}
        />
      </label>
      {error && (
        <p id={`${id}-error`} className="lcr-people-error" role="alert">
          {error}
        </p>
      )}
      <div className="lcr-people-actions">
        <button type="submit" className="lcr-small-button lcr-people-danger" disabled={busy}>
          {busy ? "Removing…" : "Remove workspace"}
        </button>
        <button type="button" className="lcr-small-button lcr-ghost" onClick={props.onCancel}>
          Keep it
        </button>
      </div>
    </form>
  );
}
